// src/components/copiloto/CopilotoRelatorio.js — o card de relatório do Copiloto.
//
// Out/26 (Cetto Copiloto, fase 2). Junta duas fontes, de propósito:
//   - o TEXTO vem do modelo (a análise em linguagem natural);
//   - os NÚMEROS dos quatro indicadores e do gráfico vêm de `relatorio`,
//     o dicionário que o backend montou a partir do banco
//     (services/relatorio_operacao). Se o modelo errar uma conta ao
//     escrever, o card continua mostrando o número certo.
//
// "Exportar" abre a impressão do navegador num iframe invisível — dali o
// "Salvar como PDF" é nativo em qualquer navegador, sem biblioteca de PDF
// no bundle e sem pop-up (que o bloqueador derrubaria).
import { useState } from 'react';
import { Box } from '@mui/material';
import { FiFileText, FiCopy, FiDownload } from 'react-icons/fi';
import { C, FONT_NUM, FONT_TXT, ACENTO, T } from './tema';

const VERDE = { c: C.green, bg: T.verdeBg };
const VERMELHO = { c: C.red, bg: T.vermelhoBg('rgba(255,69,96,0.12)') };
const NEUTRO = { c: C.text2, bg: T.neutroBg('rgba(255,255,255,0.05)') };

const num = (n) => (n ?? 0).toLocaleString('pt-BR');
const dec = (n) => n.toLocaleString('pt-BR', { maximumFractionDigits: 1 });

export function duracao(seg) {
  if (seg === null || seg === undefined) return '—';
  const s = Math.round(seg);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  const h = Math.floor(s / 3600);
  return `${h}h ${Math.floor((s % 3600) / 60)}m`;
}

// ▲/▼ com a cor do que é BOM pra métrica: tempo de resposta caindo é
// verde, conversão caindo é vermelho. Sem período anterior não há o que
// comparar — e "Estável" é diferente de "sem base".
function variacao(v, { menorMelhor = false, unidade = '%' } = {}) {
  if (v === null || v === undefined) return { d: 'Sem base anterior', ...NEUTRO };
  if (Math.abs(v) < 0.05) return { d: 'Estável', ...NEUTRO };
  const sobe = v > 0;
  const bom = menorMelhor ? !sobe : sobe;
  return { d: `${sobe ? '▲ +' : '▼ '}${dec(v)}${unidade}`, ...(bom ? VERDE : VERMELHO) };
}

function indicadores(r) {
  const tma = r.tempo_resposta_ao_template || {};
  const conv = r.conversao_templates || {};
  const saude = r.saude || {};
  const pontosSaude =
    saude.media_pct != null && saude.anterior_media_pct != null
      ? Math.round((saude.media_pct - saude.anterior_media_pct) * 10) / 10
      : null;
  return [
    {
      l: 'Contatos atendidos',
      v: num(r.contatos_atendidos?.valor),
      ...variacao(r.contatos_atendidos?.variacao_pct),
    },
    {
      l: 'Resposta ao template',
      v: duracao(tma.media_segundos),
      ...(tma.media_segundos == null
        ? { d: 'Sem respostas', ...NEUTRO }
        : variacao(tma.variacao_pct, { menorMelhor: true })),
    },
    {
      l: 'Conversão',
      v: conv.taxa_pct == null ? '—' : `${dec(conv.taxa_pct)}%`,
      ...(conv.taxa_pct == null
        ? { d: 'Sem templates', ...NEUTRO }
        : variacao(conv.variacao_pontos, { unidade: ' pts' })),
    },
    {
      l: 'Saúde média',
      v: saude.media_pct == null ? '—' : `${dec(saude.media_pct)}%`,
      ...variacao(pontosSaude, { unidade: ' pts' }),
    },
  ];
}

function textoParaCopiar(texto, r) {
  const linhas = [
    `Relatório de operação — ${r.periodo.rotulo} · ${r.periodo.chips}`,
    '',
    ...(texto || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean).flatMap((p) => [p, '']),
    ...indicadores(r).map((k) => `${k.l}: ${k.v} (${k.d})`),
    `Mensagens no período: ${num(r.mensagens.total)}`,
  ];
  return linhas.join('\n');
}

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

// Documento próprio, tema CLARO: relatório exportado vai pra impressora
// ou pra um e-mail, e fundo escuro em PDF é tinta e ilegibilidade.
function exportar(texto, r) {
  const ks = indicadores(r);
  const serie = r.mensagens.serie || [];
  const max = Math.max(1, ...serie.map((b) => b.total));
  const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
<title>Relatório Cetto — ${esc(r.periodo.rotulo)}</title>
<style>
  body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#0F172A;margin:32px;}
  h1{font-size:18px;margin:0 0 2px} .sub{color:#64748B;font-size:12px;margin-bottom:18px}
  p{font-size:13px;line-height:1.6;margin:0 0 10px;max-width:68ch}
  .k{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:18px 0}
  .k div{border:1px solid #E2E8F0;border-radius:8px;padding:10px 12px}
  .k small{display:block;font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:#64748B}
  .k b{display:block;font-size:20px;margin:4px 0} .k span{font-size:10px;color:#475569}
  table{border-collapse:collapse;font-size:11px;margin-top:6px}
  td{padding:3px 8px 3px 0;vertical-align:middle} .bar{height:9px;background:#00B86B;border-radius:2px}
  footer{margin-top:24px;font-size:10px;color:#94A3B8}
</style></head><body>
<h1>Relatório de operação</h1>
<div class="sub">${esc(r.periodo.rotulo)} · ${esc(r.periodo.chips)}</div>
${(texto || '').split(/\n\s*\n/).filter((p) => p.trim()).map((p) => `<p>${esc(p.trim())}</p>`).join('')}
<div class="k">${ks.map((k) => `<div><small>${esc(k.l)}</small><b>${esc(k.v)}</b><span>${esc(k.d)}</span></div>`).join('')}</div>
<p><b>Mensagens por ${r.mensagens.agrupamento === 'semana' ? 'semana' : 'dia'}</b> — ${num(r.mensagens.total)} no período</p>
<table>${serie.map((b) => `<tr><td>${esc(b.rotulo)}</td><td style="width:240px"><div class="bar" style="width:${Math.max(1, (b.total / max) * 100)}%"></div></td><td>${num(b.total)}</td></tr>`).join('')}</table>
<footer>Gerado pelo Copiloto da Cetto System em ${esc(new Date().toLocaleString('pt-BR'))}.</footer>
</body></html>`;
  const quadro = document.createElement('iframe');
  quadro.setAttribute('aria-hidden', 'true');
  Object.assign(quadro.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(quadro);
  const doc = quadro.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  quadro.contentWindow.focus();
  setTimeout(() => {
    quadro.contentWindow.print();
    // Remove depois: alguns navegadores leem o documento enquanto o
    // diálogo de impressão está aberto.
    setTimeout(() => quadro.remove(), 60000);
  }, 50);
}

const botao = {
  display: 'flex', alignItems: 'center', gap: '5px', height: 28, px: '10px',
  borderRadius: '6px', border: `1px solid ${C.border}`, color: C.text2,
  fontFamily: FONT_TXT, fontSize: '10.88px', fontWeight: 600, cursor: 'pointer',
  transition: 'color .15s, background .15s',
  '&:hover': { color: C.text, bgcolor: T.hover },
  '&:focus-visible': { outline: `2px solid ${ACENTO.base}`, outlineOffset: 2 },
};

export default function CopilotoRelatorio({ texto, relatorio: r }) {
  const [copiado, setCopiado] = useState(false);
  const paragrafos = (texto || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const serie = r.mensagens?.serie || [];
  const max = Math.max(1, ...serie.map((b) => b.total));
  const picoData = r.mensagens?.pico?.data;
  const poucas = serie.length <= 14;
  const passoRotulo = poucas ? 1 : Math.ceil(serie.length / 8);
  const semDados = !r.mensagens?.total;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(textoParaCopiar(texto, r));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch (e) { /* clipboard bloqueado: sem feedback falso */ }
  };

  return (
    <Box sx={{ maxWidth: 760, bgcolor: C.bg, border: `1px solid ${T.cardBorda}`, borderRadius: '10px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '12px 14px', borderBottom: `1px solid ${T.linha('rgba(255,255,255,0.06)')}`, flexWrap: 'wrap' }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '7px', bgcolor: 'rgba(145,132,217,0.10)',
          border: '1px solid rgba(145,132,217,0.22)', color: ACENTO.base,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <FiFileText size={14} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <Box sx={{ fontSize: '13.12px', fontWeight: 700, color: C.text }}>Relatório de operação</Box>
          <Box sx={{ fontSize: '10.56px', color: C.text2 }}>{r.periodo.rotulo} · {r.periodo.chips}</Box>
        </Box>
        <Box component="button" type="button" onClick={copiar} sx={{ ...botao, bgcolor: 'transparent' }}>
          <FiCopy size={12} />{copiado ? 'Copiado' : 'Copiar'}
        </Box>
        <Box component="button" type="button" onClick={() => exportar(texto, r)} sx={{ ...botao, bgcolor: C.bg3 }}>
          <FiDownload size={12} />Exportar
        </Box>
      </Box>

      <Box sx={{ p: '14px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: '66ch' }}>
          {paragrafos.map((p, i) => (
            <Box component="p" key={i} sx={{ m: 0, fontSize: '12.48px', color: C.text, lineHeight: 1.65, textWrap: 'pretty' }}>{p}</Box>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 1, mt: '14px' }}>
          {indicadores(r).map((k) => (
            <Box key={k.l} sx={{ bgcolor: C.bg3, borderRadius: '8px', p: '10px 12px' }}>
              <Box sx={{ fontSize: '9.28px', fontWeight: 700, letterSpacing: '0.12em', color: C.text3, textTransform: 'uppercase' }}>{k.l}</Box>
              <Box sx={{ fontFamily: FONT_NUM, fontSize: '22.4px', fontWeight: 700, color: C.text, lineHeight: 1, letterSpacing: '-0.5px', mt: '6px' }}>{k.v}</Box>
              <Box component="span" sx={{
                display: 'inline-block', mt: '6px', fontFamily: FONT_NUM, fontSize: '9.92px', fontWeight: 600,
                p: '2px 6.4px', borderRadius: '4px', bgcolor: k.bg, color: k.c,
              }}>{k.d}</Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
            <Box component="span" sx={{ fontSize: '10.56px', color: C.text2, flex: 1 }}>
              Mensagens por {r.mensagens?.agrupamento === 'semana' ? 'semana' : 'dia'}
            </Box>
            <Box component="span" sx={{ fontFamily: FONT_NUM, fontSize: '10.56px', color: C.text3 }}>
              {semDados ? 'Sem mensagens no período' : `${num(r.mensagens.total)} no período`}
            </Box>
          </Box>
          {!semDados && (
            <Box
              role="img"
              aria-label={`Mensagens por ${r.mensagens.agrupamento === 'semana' ? 'semana' : 'dia'}: ${serie.map((b) => `${b.rotulo} ${b.total}`).join(', ')}`}
              sx={{
                display: 'grid', gridTemplateColumns: `repeat(${serie.length}, minmax(0, 1fr))`,
                gap: poucas ? '5.6px' : '3px', alignItems: 'end', height: 84,
              }}
            >
              {serie.map((b, i) => (
                <Box key={b.data} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%', justifyContent: 'flex-end', minWidth: 0 }}>
                  <Box
                    title={`${b.rotulo}: ${num(b.total)}`}
                    sx={{
                      width: '100%', height: `${Math.max(3, (b.total / max) * 100)}%`, borderRadius: '3px',
                      bgcolor: b.data === picoData ? 'rgba(0,214,124,0.22)' : 'rgba(0,214,124,0.10)',
                      border: '1px solid rgba(0,214,124,0.28)',
                      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: '3px',
                      fontFamily: FONT_NUM, fontSize: '9.6px', fontWeight: 600, color: C.green, overflow: 'hidden',
                    }}
                  >
                    {poucas && b.total > 0 ? b.total : ''}
                  </Box>
                  <Box component="span" sx={{ fontSize: '9.6px', color: C.text3, whiteSpace: 'nowrap', visibility: i % passoRotulo === 0 ? 'visible' : 'hidden' }}>
                    {b.rotulo}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
